import React, { useMemo, memo } from 'react';
import { BusinessRealtime } from '@atlassian/jira-business-entity-common';
import { useIsHighlighted } from '@atlassian/jira-business-issue-create';
import { PageVisibility } from '@atlassian/jira-business-page-visibility';
import {
	ISSUE_DELETED_EVENT,
	type RealtimeEvent,
	type IssueEventPayload,
} from '@atlassian/jira-realtime';
import { useBoardStoreActions, useGetIssueById } from '../../services/board-store';
import { useRefetchBoardData as useRefetchBoardDataOld } from '../board-data-old';
import { useCardSubtasks } from '../card-subtasks-old';

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
const BoardViewUpdater = () => {
	const { removeIssue: removeIssueOld, updateIssue } = useBoardStoreActions();

	const [getIssueById] = useGetIssueById();
	const { onIssueUpdated, onIssueDeleted } = useCardSubtasks();
	const getIsHighlighted = useIsHighlighted();
	const refetchBoardData = useRefetchBoardDataOld();

	return useMemo(
		() => (
			<>
				<BusinessRealtime
					appId="jira-business-board-view"
					onReceive={(event: RealtimeEvent) => {
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						if ((event.payload as IssueEventPayload).issueId != null) {
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							const { issueId } = event.payload as IssueEventPayload;
							const isDeleted = event.type === ISSUE_DELETED_EVENT;

							// Return early when we receive realtime events for an issue that was
							// recently created via the issue create component in the current window.
							// This avoid race conditions.
							if (getIsHighlighted(issueId)) {
								return;
							}

							const isExistingIssue = getIssueById(issueId) != null;

							if (isExistingIssue) {
								updateIssue(issueId, { needsExtendedFieldsUpdate: true });
							}

							if (isExistingIssue && isDeleted) {
								// if issue is not a subtask and has been deleted we can simply remove
								removeIssueOld(issueId);
							} else {
								// handle subtask update
								if (isDeleted) {
									onIssueDeleted(issueId);
								} else {
									onIssueUpdated(issueId);
								}

								refetchBoardData();
							}
						}
					}}
				/>
				<PageVisibility
					onChange={({ isVisible }) => {
						if (isVisible) {
							refetchBoardData();
						}
					}}
				/>
			</>
		),
		[
			getIsHighlighted,
			getIssueById,
			onIssueDeleted,
			onIssueUpdated,
			refetchBoardData,
			removeIssueOld,
			updateIssue,
		],
	);
};

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export default memo(BoardViewUpdater);
